import {
  INTEGRATION_KINDS,
  type AlertRuleView,
  type ApiKeyDto,
  type HeartbeatConfig,
  type InstantlyConnection,
  type InstantlyConnectionSecretResponse,
  type Integration,
  type TenantFlagsResponse,
} from '@platform/contracts';
import {
  Badge,
  Button,
  Callout,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Textarea,
} from '@platform/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Copy, KeyRound, Plug, Plus, RefreshCw, Trash2 } from 'lucide-react';
import { type SubmitEvent, useId, useState } from 'react';
import { toast } from 'sonner';

import { BrandingTab } from '../components/branding-tab';
import { Field, FormError, fd } from '../components/form';
import { QualifyBudgetTab } from '../components/qualify-budget';
import { Guard, PageHeader } from '../components/page';
import { Loadable, TenantPicker, useTenantPick } from '../components/states';
import { api, errorMessage } from '../lib/api';
import { fmtDateTime } from '../lib/console';
import { fmtAgo } from '../lib/format';
import { useMe } from '../lib/queries';

/** Muestra UNA vez un secreto recién generado (API key, secreto del webhook). */
function SecretDialog({
  secret,
  title,
  extra,
  onClose,
}: {
  secret: string | null;
  title: string;
  extra?: string | undefined;
  onClose: () => void;
}) {
  return (
    <Dialog
      open={secret !== null}
      onOpenChange={(v) => {
        if (!v) onClose();
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            Copialo ahora: por seguridad no se vuelve a mostrar (se guarda sólo el hash).
          </DialogDescription>
        </DialogHeader>
        <code className="block rounded-md bg-muted p-3 font-mono text-xs break-all">{secret}</code>
        {extra ? <p className="text-xs break-all text-muted-foreground">{extra}</p> : null}
        <DialogFooter>
          <Button
            onClick={() => {
              void navigator.clipboard.writeText(secret ?? '').then(() => {
                toast.success('Copiado');
              });
            }}
          >
            <Copy /> Copiar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ------------------------------------------------------------------ Integraciones

function IntegrationsTab({ tenantId, canWrite }: { tenantId: string; canWrite: boolean }) {
  const qc = useQueryClient();
  const [kind, setKind] = useState<string>('legacy_db');
  const [error, setError] = useState<string | null>(null);
  const kindId = useId();
  const cfgId = useId();
  const list = useQuery({
    queryKey: ['integrations', tenantId],
    queryFn: () => api<Integration[]>(`/api/tenants/${tenantId}/integrations`),
  });
  const create = useMutation({
    mutationFn: (body: unknown) =>
      api<Integration>(`/api/tenants/${tenantId}/integrations`, { method: 'POST', body }),
    onSuccess: () => {
      toast.success('Integración agregada');
      void qc.invalidateQueries({ queryKey: ['integrations', tenantId] });
    },
  });
  const remove = useMutation({
    mutationFn: (id: string) =>
      api(`/api/tenants/${tenantId}/integrations/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      toast.success('Integración eliminada');
      void qc.invalidateQueries({ queryKey: ['integrations', tenantId] });
    },
    onError: (err) => toast.error(errorMessage(err)),
  });
  const onSubmit = (e: SubmitEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const f = new FormData(form);
    let config: unknown = {};
    const raw = fd(f, 'config').trim();
    if (raw) {
      try {
        config = JSON.parse(raw);
      } catch {
        setError('La configuración tiene que ser JSON válido');
        return;
      }
    }
    setError(null);
    const secret = fd(f, 'secret');
    create.mutate(
      { kind, label: fd(f, 'label'), config, ...(secret ? { secret } : {}) },
      {
        onSuccess: () => {
          form.reset();
        },
      },
    );
  };
  return (
    <div className="flex flex-col gap-6">
      <Loadable isLoading={list.isLoading} error={list.error} onRetry={() => void list.refetch()}>
        {list.data?.length ? (
          <Card>
            <CardContent className="overflow-x-auto p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Tipo</TableHead>
                    <TableHead>Nombre</TableHead>
                    <TableHead>Secreto</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead className="w-12" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {list.data.map((i) => (
                    <TableRow key={i.id}>
                      <TableCell>
                        <Badge variant="outline">{i.kind}</Badge>
                        {typeof i.config.role === 'string' ? (
                          <Badge variant="secondary" className="ml-1">
                            {i.config.role}
                          </Badge>
                        ) : null}
                      </TableCell>
                      <TableCell className="font-medium">{i.label}</TableCell>
                      <TableCell className="font-mono text-xs">
                        {i.hasSecret ? `configurado ••••${i.secretLast4 ?? ''}` : '—'}
                      </TableCell>
                      <TableCell>
                        <Badge variant={i.status === 'active' ? 'success' : 'secondary'}>
                          {i.status === 'active' ? 'activa' : 'deshabilitada'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {canWrite ? (
                          <Button
                            variant="ghost"
                            size="icon"
                            aria-label={`Eliminar ${i.label}`}
                            onClick={() => {
                              if (confirm(`¿Eliminar la integración "${i.label}"?`))
                                remove.mutate(i.id);
                            }}
                          >
                            <Trash2 />
                          </Button>
                        ) : null}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        ) : (
          <EmptyState icon={<Plug />} title="Todavía no hay integraciones" />
        )}
      </Loadable>
      {canWrite ? (
        <Card>
          <CardHeader>
            <CardTitle icon={<Plus />}>Agregar integración</CardTitle>
            <CardDescription>
              Para la escritura hacia el stack: tipo <code>legacy_db</code> con configuración{' '}
              <code>{'{"schema":"leads","role":"writer"}'}</code> y la connection string de{' '}
              <code>platform_writer</code> como secreto.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={onSubmit} className="grid gap-4 md:grid-cols-2">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor={kindId}>Tipo</Label>
                <Select value={kind} onValueChange={setKind}>
                  <SelectTrigger id={kindId}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {INTEGRATION_KINDS.map((k) => (
                      <SelectItem key={k} value={k}>
                        {k}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Field label="Nombre" name="label" placeholder="DB sweet (writer)" required />
              <Field
                label="Secreto"
                name="secret"
                type="password"
                autoComplete="off"
                hint="Se guarda cifrado y no se vuelve a mostrar."
              />
              <div className="flex flex-col gap-1.5">
                <Label htmlFor={cfgId}>Configuración (JSON, sin secretos)</Label>
                <Textarea
                  id={cfgId}
                  name="config"
                  placeholder='{"schema":"leads"}'
                  className="font-mono text-xs"
                />
              </div>
              <div className="md:col-span-2">
                <FormError message={error ?? (create.error ? errorMessage(create.error) : null)} />
                <Button type="submit" disabled={create.isPending}>
                  Agregar integración
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}

// ------------------------------------------------------------------ Instantly

function InstantlyTab({ tenantId, canWrite }: { tenantId: string; canWrite: boolean }) {
  const qc = useQueryClient();
  const [secret, setSecret] = useState<{ value: string; url: string } | null>(null);
  const [rotateFor, setRotateFor] = useState<string | null>(null);
  const [mapFor, setMapFor] = useState<string | null>(null);
  const base = `/api/tenants/${tenantId}/instantly-connections`;
  const list = useQuery({
    queryKey: ['instantly', tenantId],
    queryFn: () => api<InstantlyConnection[]>(base),
  });
  const refresh = () => qc.invalidateQueries({ queryKey: ['instantly', tenantId] });
  const create = useMutation({
    mutationFn: (body: unknown) =>
      api<InstantlyConnectionSecretResponse>(base, { method: 'POST', body }),
    onSuccess: (r) => {
      setSecret({ value: r.webhookSecret, url: r.webhookUrl });
      void refresh();
    },
    onError: (err) => toast.error(errorMessage(err)),
  });
  const rotate = useMutation({
    mutationFn: (id: string) =>
      api<InstantlyConnectionSecretResponse>(`${base}/${id}/rotate-secret`, { method: 'POST' }),
    onSuccess: (r) => {
      setSecret({ value: r.webhookSecret, url: r.webhookUrl });
      void refresh();
    },
    onError: (err) => toast.error(errorMessage(err)),
  });
  const setKey = useMutation({
    mutationFn: (v: { id: string; apiKey: string }) =>
      api(`${base}/${v.id}/api-key`, { method: 'PUT', body: { apiKey: v.apiKey } }),
    onSuccess: () => {
      toast.success('API key de Instantly actualizada');
      setRotateFor(null);
      void refresh();
    },
    onError: (err) => toast.error(errorMessage(err)),
  });
  const mapCampaign = useMutation({
    mutationFn: (v: { id: string; externalId: string; name: string }) =>
      api(`${base}/${v.id}/campaigns`, {
        method: 'POST',
        body: { externalId: v.externalId, name: v.name },
      }),
    onSuccess: () => {
      toast.success('Campaña mapeada');
      setMapFor(null);
      void refresh();
    },
    onError: (err) => toast.error(errorMessage(err)),
  });
  return (
    <div className="flex flex-col gap-6">
      <Loadable isLoading={list.isLoading} error={list.error} onRetry={() => void list.refetch()}>
        {list.data?.length ? (
          list.data.map((c) => (
            <Card key={c.id}>
              <CardHeader className="flex flex-row flex-wrap items-start gap-3">
                <div className="flex-1">
                  <CardTitle icon={<Plug />} className="flex-wrap">
                    {c.label}
                    {c.isShared ? (
                      <Badge variant="secondary">
                        {c.isOwner ? 'compartida' : `compartida · de ${c.ownerTenantName}`}
                      </Badge>
                    ) : null}
                    <Badge variant={c.status === 'active' ? 'success' : 'secondary'}>
                      {c.status}
                    </Badge>
                  </CardTitle>
                  <CardDescription>
                    {c.isOwner ? (
                      <>
                        API key {c.hasApiKey ? `••••${c.apiKeyLast4 ?? ''}` : 'sin cargar'} · último
                        evento {fmtAgo(c.lastEventAt)} · webhook <code>{c.webhookPath}</code>
                      </>
                    ) : (
                      <>
                        Conexión compartida · workspace de <strong>{c.ownerTenantName}</strong> ·
                        último evento {fmtAgo(c.lastEventAt)}
                      </>
                    )}
                  </CardDescription>
                </div>
                {canWrite && c.isOwner ? (
                  <div className="flex flex-wrap gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setRotateFor(c.id);
                      }}
                    >
                      <KeyRound /> API key
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        if (
                          confirm(
                            '¿Rotar el secreto del webhook? Hay que actualizarlo en Instantly enseguida.',
                          )
                        ) {
                          rotate.mutate(c.id);
                        }
                      }}
                    >
                      <RefreshCw /> Rotar secreto
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setMapFor(c.id);
                      }}
                    >
                      <Plus /> Campaña
                    </Button>
                  </div>
                ) : null}
              </CardHeader>
              <CardContent>
                {c.campaigns.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    {c.isOwner
                      ? 'Sin campañas mapeadas.'
                      : 'Sin campañas de esta empresa en esta conexión todavía.'}
                  </p>
                ) : (
                  <ul className="flex flex-col gap-1 text-sm">
                    {c.campaigns.map((cp) => (
                      <li key={cp.id}>
                        {cp.name}{' '}
                        <code className="text-xs text-muted-foreground">{cp.externalId}</code>
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>
          ))
        ) : (
          <EmptyState
            icon={<Plug />}
            title="Todavía no tiene campaña en Instantly"
            description={
              canWrite
                ? 'Crea una conexión propia con el formulario de abajo, o pide a la empresa dueña de un workspace compartido que mapee acá una campaña de esta empresa.'
                : 'Todavía nadie mapeó una campaña de Instantly para esta empresa. Pídeselo a tu agencia.'
            }
          />
        )}
      </Loadable>
      {canWrite ? (
        <Card>
          <CardHeader>
            <CardTitle icon={<Plus />}>Nueva conexión de Instantly</CardTitle>
          </CardHeader>
          <CardContent>
            <form
              className="grid gap-4 md:grid-cols-2"
              onSubmit={(e) => {
                e.preventDefault();
                const f = new FormData(e.currentTarget);
                const apiKey = fd(f, 'apiKey');
                create.mutate({
                  label: fd(f, 'label'),
                  ...(apiKey ? { apiKey } : {}),
                  workspaceId: fd(f, 'workspaceId') || undefined,
                  isShared: f.get('isShared') === 'on',
                });
              }}
            >
              <Field label="Nombre" name="label" placeholder="Instantly principal" required />
              <Field label="Workspace (opcional)" name="workspaceId" />
              <Field label="API key (opcional)" name="apiKey" type="password" autoComplete="off" />
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" name="isShared" /> Conexión compartida entre empresas
              </label>
              <div className="md:col-span-2">
                <Button type="submit" disabled={create.isPending}>
                  Crear conexión
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      ) : null}
      <SecretDialog
        secret={secret?.value ?? null}
        title="Secreto del webhook"
        extra={secret ? `URL del webhook: ${secret.url} (header x-ingest-token)` : undefined}
        onClose={() => {
          setSecret(null);
        }}
      />
      <Dialog
        open={rotateFor !== null}
        onOpenChange={(v) => {
          if (!v) setRotateFor(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reemplazar la API key de Instantly</DialogTitle>
            <DialogDescription>
              La anterior deja de usarse en el próximo ciclo del worker.
            </DialogDescription>
          </DialogHeader>
          <form
            className="flex flex-col gap-4"
            onSubmit={(e) => {
              e.preventDefault();
              if (rotateFor)
                setKey.mutate({
                  id: rotateFor,
                  apiKey: fd(new FormData(e.currentTarget), 'apiKey'),
                });
            }}
          >
            <Field
              label="API key nueva"
              name="apiKey"
              type="password"
              autoComplete="off"
              required
              minLength={10}
            />
            <DialogFooter>
              <Button type="submit" disabled={setKey.isPending}>
                Guardar
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      <Dialog
        open={mapFor !== null}
        onOpenChange={(v) => {
          if (!v) setMapFor(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Mapear campaña de Instantly</DialogTitle>
          </DialogHeader>
          <form
            className="flex flex-col gap-4"
            onSubmit={(e) => {
              e.preventDefault();
              const f = new FormData(e.currentTarget);
              if (mapFor)
                mapCampaign.mutate({
                  id: mapFor,
                  externalId: fd(f, 'externalId'),
                  name: fd(f, 'name'),
                });
            }}
          >
            <Field label="Id de la campaña en Instantly" name="externalId" required />
            <Field label="Nombre" name="name" required />
            <DialogFooter>
              <Button type="submit" disabled={mapCampaign.isPending}>
                Mapear
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ------------------------------------------------------------------ API keys

function ApiKeysTab({ tenantId, canWrite }: { tenantId: string; canWrite: boolean }) {
  const qc = useQueryClient();
  const [secret, setSecret] = useState<string | null>(null);
  const list = useQuery({
    queryKey: ['api-keys', tenantId],
    queryFn: () => api<ApiKeyDto[]>(`/api/tenants/${tenantId}/api-keys`),
  });
  const create = useMutation({
    mutationFn: (name: string) =>
      api<{ apiKey: string }>(`/api/tenants/${tenantId}/api-keys`, {
        method: 'POST',
        body: { name },
      }),
    onSuccess: (r) => {
      setSecret(r.apiKey);
      void qc.invalidateQueries({ queryKey: ['api-keys', tenantId] });
    },
    onError: (err) => toast.error(errorMessage(err)),
  });
  const revoke = useMutation({
    mutationFn: (id: string) =>
      api(`/api/tenants/${tenantId}/api-keys/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      toast.success('API key revocada');
      void qc.invalidateQueries({ queryKey: ['api-keys', tenantId] });
    },
    onError: (err) => toast.error(errorMessage(err)),
  });
  return (
    <div className="flex flex-col gap-4">
      <Callout tone="info">
        Keys para que n8n y los crons de esta empresa reporten a la plataforma (
        <code>/api/ingest/*</code>) y consulten si pueden ingerir/cualificar/enviar (
        <code>/api/ingest/entitlements</code>).
      </Callout>
      <Loadable isLoading={list.isLoading} error={list.error}>
        {list.data?.length ? (
          <Card>
            <CardContent className="overflow-x-auto p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nombre</TableHead>
                    <TableHead>Prefijo</TableHead>
                    <TableHead>Último uso</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead className="w-12" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {list.data.map((k) => (
                    <TableRow key={k.id}>
                      <TableCell className="font-medium">{k.name}</TableCell>
                      <TableCell className="font-mono text-xs">{k.prefix}…</TableCell>
                      <TableCell>{fmtAgo(k.lastUsedAt)}</TableCell>
                      <TableCell>
                        <Badge variant={k.revokedAt ? 'secondary' : 'success'}>
                          {k.revokedAt ? 'revocada' : 'activa'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {canWrite && !k.revokedAt ? (
                          <Button
                            variant="ghost"
                            size="icon"
                            aria-label={`Revocar ${k.name}`}
                            onClick={() => {
                              if (confirm(`¿Revocar "${k.name}"? Lo que la use deja de reportar.`))
                                revoke.mutate(k.id);
                            }}
                          >
                            <Trash2 />
                          </Button>
                        ) : null}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        ) : (
          <EmptyState icon={<KeyRound />} title="Sin API keys" />
        )}
      </Loadable>
      {canWrite ? (
        <form
          className="flex flex-wrap items-end gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            const form = e.currentTarget;
            create.mutate(fd(new FormData(form), 'name'), {
              onSuccess: () => {
                form.reset();
              },
            });
          }}
        >
          <Field label="Nombre de la key nueva" name="name" placeholder="n8n sweet" required />
          <Button type="submit" disabled={create.isPending}>
            <Plus /> Crear API key
          </Button>
        </form>
      ) : null}
      <SecretDialog
        secret={secret}
        title="API key nueva"
        onClose={() => {
          setSecret(null);
        }}
      />
    </div>
  );
}

// ------------------------------------------------------------------ Funciones (flags)

function FlagsTab({ tenantId, canWrite }: { tenantId: string; canWrite: boolean }) {
  const me = useMe();
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['flags', tenantId],
    queryFn: () => api<TenantFlagsResponse>(`/api/tenants/${tenantId}/flags`),
  });
  const set = useMutation({
    mutationFn: (v: { key: string; enabled: boolean }) =>
      api<TenantFlagsResponse>(`/api/tenants/${tenantId}/flags`, {
        method: 'PUT',
        body: { flags: { [v.key]: v.enabled } },
      }),
    onSuccess: (r) => {
      qc.setQueryData(['flags', tenantId], r);
      toast.success('Guardado');
    },
    onError: (err) => toast.error(errorMessage(err)),
  });
  return (
    <Loadable isLoading={q.isLoading} error={q.error}>
      <div className="flex flex-col gap-3">
        {q.data?.flags.map((f) => {
          const editable = canWrite && (!f.superadminOnly || me.user.isSuperadmin);
          return (
            <Card key={f.key}>
              <CardContent className="flex items-start gap-4 p-4">
                <div className="flex-1">
                  <p className="font-medium">{f.label}</p>
                  <p className="text-sm text-muted-foreground">{f.description}</p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    <code>{f.key}</code>
                    {f.isDefault ? ' · valor por defecto' : ''}
                    {f.superadminOnly ? ' · sólo superadmin' : ''}
                  </p>
                </div>
                <Switch
                  aria-label={f.label}
                  checked={f.enabled}
                  disabled={!editable || set.isPending}
                  onCheckedChange={(v) => {
                    if (
                      f.key === 'console.legacy_writes' &&
                      v &&
                      !confirm('¿Habilitar la escritura hacia el stack de esta empresa?')
                    )
                      return;
                    set.mutate({ key: f.key, enabled: v });
                  }}
                />
              </CardContent>
            </Card>
          );
        })}
      </div>
    </Loadable>
  );
}

// ------------------------------------------------------------------ Alertas (reglas)

const SOURCE_LABEL = { builtin: 'incorporada', agency: 'agencia', tenant: 'esta empresa' } as const;

function AlertRulesTab({ tenantId, canWrite }: { tenantId: string; canWrite: boolean }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState<AlertRuleView | null>(null);
  const base = `/api/tenants/${tenantId}/alert-rules`;
  const q = useQuery({
    queryKey: ['alert-rules', tenantId],
    queryFn: () => api<AlertRuleView[]>(base),
  });
  const save = useMutation({
    mutationFn: (v: { key: string; body: unknown }) =>
      api<AlertRuleView[]>(`${base}/${v.key}`, { method: 'PUT', body: v.body }),
    onSuccess: (r) => {
      qc.setQueryData(['alert-rules', tenantId], r);
      setEditing(null);
      toast.success('Regla guardada');
    },
    onError: (err) => toast.error(errorMessage(err)),
  });
  const reset = useMutation({
    mutationFn: (key: string) => api<AlertRuleView[]>(`${base}/${key}`, { method: 'DELETE' }),
    onSuccess: (r) => {
      qc.setQueryData(['alert-rules', tenantId], r);
      toast.success('Volvió al valor de la agencia');
    },
    onError: (err) => toast.error(errorMessage(err)),
  });
  return (
    <Loadable isLoading={q.isLoading} error={q.error}>
      <Card>
        <CardContent className="overflow-x-auto p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Regla</TableHead>
                <TableHead>Activa</TableHead>
                <TableHead>Severidad</TableHead>
                <TableHead className="hidden sm:table-cell">Re-aviso</TableHead>
                <TableHead className="hidden md:table-cell">Origen</TableHead>
                <TableHead className="w-40" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {q.data?.map((r) => (
                <TableRow key={r.ruleKey}>
                  <TableCell>
                    <code className="text-xs">{r.ruleKey}</code>
                  </TableCell>
                  <TableCell>{r.effective.enabled ? 'Sí' : 'No'}</TableCell>
                  <TableCell>
                    <Badge
                      variant={
                        r.effective.severity === 'critical'
                          ? 'destructive'
                          : r.effective.severity === 'warning'
                            ? 'warning'
                            : 'secondary'
                      }
                    >
                      {r.effective.severity}
                    </Badge>
                  </TableCell>
                  <TableCell className="hidden sm:table-cell">
                    {r.effective.cooldownMinutes} min
                  </TableCell>
                  <TableCell className="hidden md:table-cell">{SOURCE_LABEL[r.source]}</TableCell>
                  <TableCell>
                    {canWrite ? (
                      <div className="flex gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            setEditing(r);
                          }}
                        >
                          Editar
                        </Button>
                        {r.source === 'tenant' ? (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              reset.mutate(r.ruleKey);
                            }}
                          >
                            Restablecer
                          </Button>
                        ) : null}
                      </div>
                    ) : null}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
      <Dialog
        open={editing !== null}
        onOpenChange={(v) => {
          if (!v) setEditing(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Regla {editing?.ruleKey}</DialogTitle>
            <DialogDescription>Pisa la regla sólo para esta empresa.</DialogDescription>
          </DialogHeader>
          {editing ? (
            <form
              className="flex flex-col gap-4"
              onSubmit={(e) => {
                e.preventDefault();
                const f = new FormData(e.currentTarget);
                let params: unknown;
                try {
                  params = JSON.parse(fd(f, 'params') || '{}');
                } catch {
                  toast.error('Parámetros: JSON inválido');
                  return;
                }
                save.mutate({
                  key: editing.ruleKey,
                  body: {
                    enabled: f.get('enabled') === 'on',
                    severity: fd(f, 'severity'),
                    cooldownMinutes: Number(fd(f, 'cooldown')),
                    channels: { telegram: f.get('telegram') === 'on' },
                    params,
                  },
                });
              }}
            >
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" name="enabled" defaultChecked={editing.effective.enabled} />{' '}
                Activa
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  name="telegram"
                  defaultChecked={editing.effective.channels.telegram !== false}
                />{' '}
                Avisar por Telegram
              </label>
              <div className="flex flex-col gap-1">
                <Label htmlFor="rule-severity">Severidad</Label>
                <select
                  id="rule-severity"
                  name="severity"
                  defaultValue={editing.effective.severity}
                  className="h-9 rounded-md border border-input bg-card px-2 text-sm"
                >
                  <option value="info">info</option>
                  <option value="warning">warning</option>
                  <option value="critical">critical</option>
                </select>
              </div>
              <Field
                label="Re-aviso (minutos)"
                name="cooldown"
                type="number"
                min={1}
                defaultValue={editing.effective.cooldownMinutes}
                required
              />
              <div className="flex flex-col gap-1">
                <Label htmlFor="rule-params">Parámetros (JSON)</Label>
                <Textarea
                  id="rule-params"
                  name="params"
                  className="font-mono text-xs"
                  defaultValue={JSON.stringify(editing.effective.params, null, 2)}
                />
              </div>
              <DialogFooter>
                <Button type="submit" disabled={save.isPending}>
                  Guardar
                </Button>
              </DialogFooter>
            </form>
          ) : null}
        </DialogContent>
      </Dialog>
    </Loadable>
  );
}

// ------------------------------------------------------------------ Heartbeats

function HeartbeatsTab({ tenantId, canWrite }: { tenantId: string; canWrite: boolean }) {
  const qc = useQueryClient();
  const base = `/api/tenants/${tenantId}/heartbeats`;
  const q = useQuery({
    queryKey: ['heartbeats', tenantId],
    queryFn: () => api<HeartbeatConfig[]>(base),
  });
  const refresh = () => qc.invalidateQueries({ queryKey: ['heartbeats', tenantId] });
  const create = useMutation({
    mutationFn: (body: unknown) => api(base, { method: 'POST', body }),
    onSuccess: () => {
      toast.success('Expectativa creada');
      void refresh();
    },
    onError: (err) => toast.error(errorMessage(err)),
  });
  const patch = useMutation({
    mutationFn: (v: { id: string; body: unknown }) =>
      api(`${base}/${v.id}`, { method: 'PATCH', body: v.body }),
    onSuccess: () => void refresh(),
    onError: (err) => toast.error(errorMessage(err)),
  });
  const del = useMutation({
    mutationFn: (id: string) => api(`${base}/${id}`, { method: 'DELETE' }),
    onSuccess: () => void refresh(),
    onError: (err) => toast.error(errorMessage(err)),
  });
  return (
    <div className="flex flex-col gap-4">
      <Callout tone="info">
        Cada cron reporta un latido; si no llega en su intervalo + gracia, el watchdog abre una
        alerta.
      </Callout>
      <Loadable isLoading={q.isLoading} error={q.error}>
        {q.data?.length ? (
          <Card>
            <CardContent className="overflow-x-auto p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Job</TableHead>
                    <TableHead>Cada</TableHead>
                    <TableHead>Gracia</TableHead>
                    <TableHead>Último latido</TableHead>
                    <TableHead>Activa</TableHead>
                    <TableHead className="w-12" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {q.data.map((h) => (
                    <TableRow key={h.id}>
                      <TableCell>
                        <code className="text-xs">{h.jobKey}</code>{' '}
                        {h.overdue ? <Badge variant="destructive">atrasado</Badge> : null}
                      </TableCell>
                      <TableCell>{Math.round(h.expectedIntervalSeconds / 60)} min</TableCell>
                      <TableCell>{Math.round(h.graceSeconds / 60)} min</TableCell>
                      <TableCell title={fmtDateTime(h.lastSeenAt)}>
                        {fmtAgo(h.lastSeenAt)}
                      </TableCell>
                      <TableCell>
                        <Switch
                          aria-label={`Activar ${h.jobKey}`}
                          checked={h.enabled}
                          disabled={!canWrite}
                          onCheckedChange={(v) => {
                            patch.mutate({ id: h.id, body: { enabled: v } });
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        {canWrite ? (
                          <Button
                            variant="ghost"
                            size="icon"
                            aria-label={`Eliminar ${h.jobKey}`}
                            onClick={() => {
                              if (confirm(`¿Eliminar la expectativa de "${h.jobKey}"?`))
                                del.mutate(h.id);
                            }}
                          >
                            <Trash2 />
                          </Button>
                        ) : null}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        ) : (
          <EmptyState
            icon={<RefreshCw />}
            title="Sin expectativas de heartbeat para esta empresa"
          />
        )}
      </Loadable>
      {canWrite ? (
        <form
          className="flex flex-wrap items-end gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            const form = e.currentTarget;
            const f = new FormData(form);
            create.mutate(
              {
                jobKey: fd(f, 'jobKey'),
                expectedIntervalSeconds: Number(fd(f, 'every')) * 60,
                graceSeconds: Number(fd(f, 'grace')) * 60,
              },
              {
                onSuccess: () => {
                  form.reset();
                },
              },
            );
          }}
        >
          <Field label="Job" name="jobKey" placeholder="cron:reintentar-fallos" required />
          <Field label="Cada (min)" name="every" type="number" min={1} defaultValue={60} required />
          <Field
            label="Gracia (min)"
            name="grace"
            type="number"
            min={0}
            defaultValue={10}
            required
          />
          <Button type="submit" disabled={create.isPending}>
            <Plus /> Agregar
          </Button>
        </form>
      ) : null}
    </div>
  );
}

// ------------------------------------------------------------------ página

export function ConfigPage() {
  const { tenant, candidates, setPicked, can } = useTenantPick('config:read');
  const canWrite = can('config:write');
  return (
    <Guard requires="config:read">
      <PageHeader
        title="Integraciones"
        description="Conexiones de cada empresa (Instantly, n8n, bases de datos), API keys, funciones, reglas de alertas, tareas programadas y su logo."
        actions={<TenantPicker candidates={candidates} value={tenant?.id} onChange={setPicked} />}
      />
      {!tenant ? (
        <EmptyState
          icon={<Plug />}
          title="Sin empresas"
          description="No hay empresas con configuración visible en este alcance."
        />
      ) : (
        <Tabs defaultValue="integrations" key={tenant.id}>
          <TabsList className="mb-4 flex-wrap">
            <TabsTrigger value="integrations">Integraciones</TabsTrigger>
            <TabsTrigger value="instantly">Instantly</TabsTrigger>
            <TabsTrigger value="keys">API keys</TabsTrigger>
            <TabsTrigger value="flags">Funciones</TabsTrigger>
            <TabsTrigger value="qualify">Calificación</TabsTrigger>
            <TabsTrigger value="alerts">Alertas</TabsTrigger>
            <TabsTrigger value="heartbeats">Heartbeats</TabsTrigger>
            <TabsTrigger value="branding">Marca</TabsTrigger>
          </TabsList>
          <TabsContent value="integrations">
            <IntegrationsTab tenantId={tenant.id} canWrite={canWrite} />
          </TabsContent>
          <TabsContent value="instantly">
            <InstantlyTab tenantId={tenant.id} canWrite={canWrite} />
          </TabsContent>
          <TabsContent value="keys">
            <ApiKeysTab tenantId={tenant.id} canWrite={canWrite} />
          </TabsContent>
          <TabsContent value="flags">
            <FlagsTab tenantId={tenant.id} canWrite={canWrite} />
          </TabsContent>
          <TabsContent value="qualify">
            <QualifyBudgetTab tenantId={tenant.id} canWrite={canWrite} />
          </TabsContent>
          <TabsContent value="alerts">
            <AlertRulesTab tenantId={tenant.id} canWrite={canWrite} />
          </TabsContent>
          <TabsContent value="heartbeats">
            <HeartbeatsTab tenantId={tenant.id} canWrite={canWrite} />
          </TabsContent>
          <TabsContent value="branding">
            <BrandingTab
              tenantId={tenant.id}
              tenantName={tenant.name}
              tenantSlug={tenant.slug}
              canWrite={canWrite}
            />
          </TabsContent>
        </Tabs>
      )}
    </Guard>
  );
}
